import { Suspense, lazy, ComponentType, ReactNode } from "react";
import { useInViewport } from "@/hooks/useInViewport";
import { Skeleton } from "@/components/ui/skeleton";

interface LazySectionProps {
  children: ReactNode;
  fallback?: ReactNode;
  className?: string;
  minHeight?: string;
}

export function LazySection({
  children,
  fallback,
  className = "",
  minHeight = "200px",
}: LazySectionProps) {
  const [ref, isInViewport] = useInViewport<HTMLDivElement>({
    rootMargin: "200px",
    triggerOnce: true,
  });

  return (
    <div ref={ref} className={className} style={{ minHeight: isInViewport ? "auto" : minHeight }}>
      {isInViewport ? (
        children
      ) : (
        fallback || (
          <div className="space-y-4 animate-pulse">
            <Skeleton className="h-8 w-48" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Skeleton className="h-48 w-full rounded-md" />
              <Skeleton className="h-48 w-full rounded-md" />
              <Skeleton className="h-48 w-full rounded-md" />
            </div>
          </div>
        )
      )}
    </div>
  );
}

interface DeferredQuerySectionProps<T> {
  queryKey: string;
  enabled?: boolean;
  children: (data: T | undefined, isLoading: boolean) => ReactNode;
  fallback?: ReactNode;
  className?: string;
  minHeight?: string;
}

export function DeferredQuerySection<T>({
  children,
  fallback,
  className = "",
  minHeight = "200px",
}: DeferredQuerySectionProps<T>) {
  const [ref, isInViewport] = useInViewport<HTMLDivElement>({
    rootMargin: "200px",
    triggerOnce: true,
  });

  return (
    <div ref={ref} className={className} style={{ minHeight: isInViewport ? "auto" : minHeight }}>
      {isInViewport ? (
        children(undefined, false)
      ) : (
        fallback || (
          <div className="space-y-4 animate-pulse">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-48 w-full rounded-md" />
          </div>
        )
      )}
    </div>
  );
}
